// 1. React imports

// 2. External libraries
import { Info } from 'lucide-react';

// 3. Internal components
import { Alert, AlertDescription } from '~/components/ui/alert';
import { BrandCard } from '~/components/common';
import { CardContent } from '~/components/ui/card';
import { AuthorizationActions } from './AuthorizationActions';
import { ClientInfoSection } from './ClientInfoSection';

// 4. Type imports
import type { OAuthClientMetadata, OAuthClientSummary } from '~/api/oauth';

// 5. Custom hooks and utilities
import { CLIENT_TYPE_DISPLAY_NAMES, getConnectionTypeIcon } from '~/utils/connectionTypes';

/**
 * Props for OAuthReconnectCard component.
 *
 * Per §TS.3.2: Descriptive props interface naming.
 */
export interface OAuthReconnectCardProps {
  /** OAuth client ID requesting authorization */
  clientId: string | null;
  /** OAuth client metadata from backend */
  metadata: OAuthClientMetadata | null;
  /** Summary of existing authorized client */
  existingClient: OAuthClientSummary;
  /** Callback when user chooses to reconnect and update credentials */
  onReconnect: () => void;
  /** Callback when user cancels reconnection */
  onCancel: () => void;
  /** True while the reconnect request is in flight */
  isAuthorizing?: boolean;
}

/**
 * OAuth reconnection card for updating credentials of existing client.
 *
 * Displays when user attempts to authorize an OAuth client that already exists
 * for their account. Shows existing client info and topics, with option to
 * update credentials or cancel.
 *
 * Behavior:
 * - Displays existing client type name and icon
 * - Lists topics currently authorized for this client
 * - Shows warning about credential update
 * - Reconnect button triggers onReconnect()
 * - Cancel button triggers onCancel()
 *
 * Per §TS.3.1: Function declaration for named export.
 *
 * @example
 * ```tsx
 * <OAuthReconnectCard
 *   clientId="dd_client_123"
 *   metadata={{ clientName: "chatgpt", isRegistered: true, ... }}
 *   existingClient={{
 *     clientId: "dd_client_123",
 *     clientType: "chatgpt",
 *     displayName: "ChatGPT",
 *     authorizedAt: "2024-01-15T10:30:00Z",
 *     topics: [{ id: "topic-1", name: "Work", canRead: true, canWrite: true }]
 *   }}
 *   onReconnect={() => handleReconnect()}
 *   onCancel={() => handleCancel()}
 * />
 * ```
 */
export function OAuthReconnectCard({
  clientId,
  metadata,
  existingClient,
  onReconnect,
  onCancel,
  isAuthorizing = false,
}: OAuthReconnectCardProps) {
  // Get icon for client type
  const ClientIcon = getConnectionTypeIcon(existingClient.clientType);

  // Derive clientType from existingClient for display name lookup
  const clientType = existingClient.clientType;

  // Handle reconnect button click
  const handleReconnectClick = () => {
    onReconnect();
  };

  // Handle cancel button click
  const handleCancelClick = () => {
    onCancel();
  };

  return (
    <>
      {/* Client Info Card */}
      <BrandCard accent="top">
        <CardContent>
          <ClientInfoSection
            clientId={clientId}
            clientType={clientType}
            metadata={metadata}
            isLoading={false}
          />
        </CardContent>
      </BrandCard>

      {/* Existing Authorization Card */}
      <BrandCard accent="top" className="mt-6">
        <CardContent>
          <h6 className="mb-2 text-lg font-semibold">Existing Authorization</h6>
          <div className="mb-4 flex items-center gap-4">
            {ClientIcon}
            <div>
              <p className="text-base">
                <strong>
                  {CLIENT_TYPE_DISPLAY_NAMES[existingClient.clientType] ||
                    existingClient.displayName}
                </strong>
              </p>
              <p className="text-xs text-muted-foreground">
                Authorized on{' '}
                {new Date(existingClient.authorizedAt).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>
          </div>

          {/* Topics list */}
          {existingClient.topics.length > 0 && (
            <div className="mt-4">
              <p className="mb-2 text-sm text-muted-foreground">Authorized Topics:</p>
              <ul className="list-none" data-testid="oauth-reconnect-topics-list">
                {existingClient.topics.map((topic) => (
                  <li key={topic.id} className="py-1 text-sm">
                    {topic.name}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Info about credential update - not an error, no alert role */}
          <Alert className="mt-4">
            <Info data-icon="inline-start" />
            <AlertDescription>
              Reconnecting will update the credentials for this existing authorization. Your current
              topics and permissions will remain unchanged.
            </AlertDescription>
          </Alert>
        </CardContent>
      </BrandCard>

      {/* Authorization Actions */}
      <AuthorizationActions
        onAuthorize={handleReconnectClick}
        onCancel={handleCancelClick}
        disabled={false}
        buttonText="Reconnect"
        isAuthorizing={isAuthorizing}
      />
    </>
  );
}
